import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import Helmet from 'react-helmet';
import Fa from 'react-fontawesome';
import moment from 'moment';
import { delay, reduce } from 'lodash';
import { Button } from 'react-bootstrap';
import { Redirect, Link } from 'react-router-dom';
import ProgressBar from '../../../../components/progress-bar';
import { viewTeam, deleteMember, updateMember } from '../../TeamActions';
import { getTeam, getAllTeamOwners } from '../../TeamReducer';
import MemberModal from '../../components/MemberModal';
import SearchField from '../../../../components/search-field';
import OwnerRenderer from '../../components/table-cells/MemberRenderer';
import ActionRenderer from '../../components/table-cells/MemberActionRenderer';
import DateRenderer from '../../components/table-cells/DateRenderer';
import ImportMembersModal from '../../components/import-members-modal';
import NumberDisplay from '../../../../components/number-display';

class TeamDetailPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      loaded: false,
      showModal: false,
      cacheDates: {}, // indexed by member _id
      deletingMembers: {}, // index by member _id
      updatingMembers: {}, // index by member _id
      totalMembers: null,
      maximumMembers: null,
    };
    this.handleSearch = this.handleSearch.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.ownerValueGetter = this.ownerValueGetter.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleAddMembers = this.handleAddMembers.bind(this);
    this.dateValueGetter = this.dateValueGetter.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
  }

  componentDidMount() {
    this.loadTeam();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match && prevProps.match.params ? prevProps.match.params.teamID : null;
    if (prevProps === undefined) {
      return;
    }
    const currentId = this.props.match && this.props.match.params ? this.props.match.params.teamID : null;
    if (currentId && prevId !== currentId) {
      this.loadTeam();
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  resizeColumns() {
    if (this.gridApi && this.gridColumnApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  ownerValueGetter(params) {
    const { data } = params;
    if (!data) {
      return null;
    }
    const { owners } = this.props;
    const owner = owners[data.owner_id];
    if (!owner) {
      return null;
    }
    return `${owner.first_name} ${owner.last_name}`;
  }

  dateValueGetter(params) {
    const { cacheDates } = this.state;
    const { data, colDef: { field } } = params;
    if (!data || !cacheDates[data._id]) {
      return null;
    }
    const dates = cacheDates[data._id];
    return dates[field];
  }

  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  handleAddMembers(newMembers) {
    this.resizeColumns();
    const { cacheDates } = this.state;
    const newCacheDates = this.fillDates(newMembers);
    this.setState({ maximumMembers: newMembers.maximumMembers, totalMembers: newMembers.totalMembers, cacheDates: { ...newCacheDates, ...cacheDates } }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  handleDateChange(member, field, date) {
    const { cacheDates, updatingMembers } = this.state;
    const { team } = this.props;
    const id = member._id;
    if (!cacheDates[id]) {
      cacheDates[id] = { start: null, end: null };
    }
    if (updatingMembers[id]) {
      return;
    }
    const newCachedDate = { ...cacheDates };
    newCachedDate[id][field] = date;
    const newUpdatingMembers = { ...updatingMembers };
    newUpdatingMembers[id] = true;
    this.setState({ cacheDates: newCachedDate, updatingMembers: newUpdatingMembers }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
      const params = {
        start: newCachedDate[id].start ? newCachedDate[id].start.format('YYYY-MM-DD') : null,
        end: newCachedDate[id].end ? newCachedDate[id].end.format('YYYY-MM-DD') : null,
      };
      this.props.dispatch(updateMember(team._id, member.owner_id, params))
        .finally(() => {
          delete newUpdatingMembers[id];
          this.setState({ updatingMembers: { ...newUpdatingMembers } }, () => {
            if (this.gridApi) {
              this.gridApi.refreshCells({ force: true });
            }
            this.resizeColumns();
          });
        });
    });
  }

  handleDelete(member) {
    const { team } = this.props;
    const { deletingMembers } = this.state;
    if (!deletingMembers[member._id]) {
      const newDeletingMembers = { ...deletingMembers };
      newDeletingMembers[member._id] = true;
      this.setState({ deletingMembers: newDeletingMembers }, () => {
        this.resizeColumns();
        this.props.dispatch(deleteMember(team._id, member.owner_id))
          .then(
            ({ totalMembers, maximumMembers }) => {
              const newState = { ...deletingMembers };
              delete newState[member._id];
              this.setState({ totalMembers, maximumMembers, deletingMembers: newState }, () => {
                if (this.gridApi) {
                  this.gridApi.refreshCells({ force: true });
                }
              });
              this.resizeColumns();
            },
            () => {
              const newState = { ...deletingMembers };
              delete newState[member._id];
              this.setState({ deletingMembers: newState });
              this.resizeColumns();
            },
          );
      });
    }
  }

  loadTeam() {
    const id = this.props.match && this.props.match.params ? this.props.match.params.teamID : null;
    this.setState({ loading: true, loaded: false });
    this.props.dispatch(viewTeam(id))
      .then(
        ({ team, totalMembers, maximumMembers }) => {
          const cacheDates = this.fillDates(team.members);
          this.setState({ totalMembers, maximumMembers, loading: false, loaded: true, cacheDates }, () => {
            delay(() => {
              this.resizeColumns();
            }, 500);
          });
        },
        () => {
          // TODO: display error in toast
          this.setState({ loading: false, loaded: false });
        },
      );
  }

  fillDates(members) {
    return reduce(members, (items, member) => {
      items[member._id] = {
        start: member.start ? moment(member.start) : null,
        end: member.end ? moment(member.end) : null,
      };
      return items;
    }, {});
  }

  renderAssignButton() {
    const { team } = this.props;
    const canManage = team.can_manage === true;
    const { totalMembers, maximumMembers } = this.state;
    if (!canManage || totalMembers >= maximumMembers) {
      return null;
    }

    return (
      <Button
        bsStyle="primary"
        onClick={() => this.setState({ showModal: true })}
      >
        <Fa name="plus" /> Assign Members
      </Button>
    );
  }

  renderCounter() {
    const { totalMembers, maximumMembers } = this.state;
    if (totalMembers === null || maximumMembers === null || totalMembers === 0) {
      return null;
    }
    const cls = totalMembers > maximumMembers ? 'text-danger' : '';
    return (
      <div className="well well-sm" style={{ margin: 0, borderRight: '1px solid #ccc', borderLeft: '1px solid #ccc' }}>
        <div className={cls}>Team size: <NumberDisplay value={totalMembers} precision={0} /> of <NumberDisplay value={maximumMembers} precision={0} />{' '}</div>
      </div>
    );
  }

  renderTable() {
    const { team, owners } = this.props;
    const { deletingMembers, cacheDates, updatingMembers, totalMembers, maximumMembers } = this.state;
    const { members } = team;
    const hasMembers = members && members.length > 0;

    const defaultColDef = {
      suppressMenu: true,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      resizable: true,
      sortable: true,
    };

    const columnDefs = [
      {
        headerName: 'Sales Reps',
        field: 'user_id',
        colId: 'member',
        pinned: 'left',
        valueGetter: this.ownerValueGetter,
        cellRenderer: OwnerRenderer,
        cellRendererParams: {
          owners,
        },
      },
      {
        headerName: 'Start Date',
        field: 'start',
        colId: 'start',
        valueGetter: this.dateValueGetter,
        cellRenderer: DateRenderer,
        cellRendererParams: {
          deletingMembers,
          updatingMembers,
          cacheDates,
          onChange: this.handleDateChange,
        },
      },
      {
        headerName: 'End Date',
        field: 'end',
        colId: 'end',
        valueGetter: this.dateValueGetter,
        cellRenderer: DateRenderer,
        cellRendererParams: {
          deletingMembers,
          updatingMembers,
          cacheDates,
          onChange: this.handleDateChange,
        },
      },
      {
        headerName: 'Remove',
        field: '_id',
        colId: 'action',
        pinned: 'right',
        cellRenderer: ActionRenderer,
        cellRendererParams: {
          handleDelete: this.handleDelete,
          deletingMembers,
          updatingMembers,
        },
      },
    ];

    return (
      <div>
        <nav className="navbar navbar-default navbar-controls">
          {hasMembers &&
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
          }
          <form className={`navbar-form ${hasMembers ? 'navbar-right' : ''}`} style={{ paddingRight: 30 }}>
            {this.renderAssignButton()}
            {' '}
            <ImportMembersModal totalMembers={totalMembers} maximumMembers={maximumMembers} team={team} />
          </form>
        </nav>
        {this.renderCounter()}
        {hasMembers &&
          <div className="ag-theme-alpine" style={{ width: '100%' }}>
            <AgGridReact
              reactNext
              animateRows
              domLayout="autoHeight"
              suppressColumnVirtualisation
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowNodeId={data => data._id}
              onGridReady={this.onGridReady}
              rowData={team.members}
              pagination
              paginationPageSize={50}
            />
          </div>
        } 
      </div>
    );
  }

  renderContent() {
    const { team } = this.props;
    const hasDescription = team.description && team.description.length > 0;
    const canManage = team.can_manage === true;

    return (
      <div>
        {!canManage && <div className="alert alert-warning">This is a global team. You don not have enough sufficiency privileges to edit global team.</div>}
        {hasDescription && <div className="well">{team.description}</div>}
        {this.renderTable()}
      </div>
    );
  }

  renderRedirect() {
    return (
      <Redirect to="/teams" />
    );
  }

  renderTitle() {
    const { loading, loaded } = this.state;
    const { team } = this.props;
    const parts = [];
    if (!loading && loaded && team) {
      parts.push(<span key="main"><Link to="teams">All Teams</Link></span>);
      parts.push(<span key="connector">&nbsp;<Fa name="chevron-right" />&nbsp;</span>);
      parts.push(<span key="sub">{team.name}</span>);
    } else {
      parts.push(<span key="main">All Teams</span>);
    }
    return (
      <div className="clearfix">
        <h1 className="page-header">
          {parts}
        </h1>
      </div>
    );
  }

  renderMemberModal() {
    const { showModal } = this.state;
    const { team } = this.props;
    if (!team) {
      return null;
    }
    return (
      <MemberModal
        show={showModal}
        team={team}
        onClose={() => this.setState({ showModal: false })}
        onSuccess={this.handleAddMembers}
      />
    );
  }

  render() {
    const { loading, loaded } = this.state;
    const { team } = this.props;
    let title = 'My Sales Teams';
    if (!loading && loaded && team) {
      title = `${team.name} | My Sales Teams`;
    }
    return (
      <div>
        <Helmet title={title} />
        {this.renderTitle()}
        {loading && <ProgressBar />}
        {!loading && <div>
          {!loaded && this.renderRedirect()}
          {loaded && team !== null && <div>
            {this.renderContent()}
            {this.renderMemberModal()}
          </div>}
        </div>}
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  const id = ownProps.match && ownProps.match.params ? ownProps.match.params.teamID : null;
  return {
    team: id ? getTeam(state, id) : undefined,
    owners: getAllTeamOwners(state),
  };
}

TeamDetailPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      teamID: PropTypes.string,
    }),
  }).isRequired,
  team: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    can_manage: PropTypes.bool,
    members: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  owners: PropTypes.shape({
  }),
};

TeamDetailPage.defaultProps = {
  team: null,
};

TeamDetailPage.contextTypes = {
  router: PropTypes.shape({}),
};

export default connect(mapStateToProps)(TeamDetailPage);
